/**
 * RBAC utility functions.
 *
 * - Control value helpers (normalize, grant check)
 * - User context derivation (extract RBAC info from raw user object)
 */

import type { ControlType } from "@/features/users/types/userTypes";
import { normalizeAccessConfigForClient } from "@/rbac/normalizeAccessConfig";

// ─── Control Values ──────────────────────────────────────────────
//
// Legacy 4 levels (still on the wire today): "allow" | "deny" | "view" | "own"
//   - "allow" → full access
//   - "deny"  → no access
//   - "view"  → read-only
//   - "own"   → same as allow in UI (server enforces row-level ownership)
//
// New 5-level dial from the role wizard redesign: "admin" | "manage" | "edit" | "view" | "deny"
//   - "admin"  → full access incl. destructive ops (treated as "allow" at runtime)
//   - "manage" → create + edit + view; no destructive ops (treated as "allow" at runtime
//                until the backend lands fine-grained enforcement)
//   - "edit"   → grants view + edit/update + create on records; not delete.
//                Wizard rows like Deadlines / Follow-ups only expose
//                "edit | view | deny", so "edit" must include create otherwise
//                the user can't actually use the feature.
//   - "view"   → read-only (matches legacy)
//   - "deny"   → no access (matches legacy)

export const ALL_CONTROL_TYPES: readonly ControlType[] = [
  "admin",
  "manage",
  "allow",
  "edit",
  "view",
  "own",
  "deny",
];

export function normalizeControlValue(raw: any): ControlType {
  if (
    raw === "admin" ||
    raw === "manage" ||
    raw === "allow" ||
    raw === "edit" ||
    raw === "view" ||
    raw === "own" ||
    raw === "deny"
  ) {
    return raw;
  }
  return "deny";
}

export function controlGrantsAction(
  control: ControlType,
  action: string
): boolean {
  if (control === "deny") return false;
  if (
    control === "admin" ||
    control === "manage" ||
    control === "allow" ||
    control === "own"
  ) {
    return true;
  }
  if (control === "edit") {
    return (
      action === "view" ||
      action === "edit" ||
      action === "update" ||
      action === "create"
    );
  }
  if (control === "view") return action === "view";
  return false;
}

// ─── User Context Derivation ─────────────────────────────────────
//
// The API returns a deeply nested user object with role.permissions[0].access_config.
// These functions extract the RBAC-relevant parts into clean flat structures.

export interface RbacUserContext {
  userId: number | null;
  tenantId: number | null;
  roleId: number | null;
  roleName: string | null;
  roleSlug: string | null;
  roleHierarchy: number | null;
  systemRole: string | null;
  permissionRecordId: number | null;
  presetRole: string | null;
  isTenantAdmin: boolean;
  isSystemAdmin: boolean;
}

export interface AccessConfig {
  global?: Record<string, Record<string, any> | undefined>;
  stages?: Array<{
    stageId: number;
    permissions: Record<string, Record<string, any> | undefined>;
  }>;
  sources?: Array<{
    sourceId: number;
    permissions: Record<string, Record<string, any> | undefined>;
  }>;
  routeKeys?: string[];
  presetRole?: string;
}

/** localStorage stores either `{ user, token }` or just the user object */
export function unwrapUser(stored: any): any {
  if (!stored || typeof stored !== "object") return null;
  return stored.user !== undefined ? stored.user : stored;
}

/** Pull access_config from user.role.permissions[0] and normalize it */
export function deriveAccessConfig(user: any): AccessConfig | null {
  const ac = user?.role?.permissions?.[0]?.access_config;
  if (!ac || typeof ac !== "object") return null;
  return normalizeAccessConfigForClient(ac);
}

/** Pull identity + role metadata from the raw user object */
export function deriveUserContext(user: any): RbacUserContext | null {
  if (!user || typeof user !== "object") return null;

  const role = user.role;
  const firstPerm = role?.permissions?.[0];
  const ac = firstPerm?.access_config;

  const systemRole =
    typeof user.systemRole === "string" ? user.systemRole : null;

  // Parse roleHierarchy — API may send it as number or string
  let roleHierarchy: number | null = null;
  const rawHierarchy = role?.hierarchy;
  if (typeof rawHierarchy === "number" && Number.isFinite(rawHierarchy)) {
    roleHierarchy = rawHierarchy;
  } else if (typeof rawHierarchy === "string" && rawHierarchy.trim() !== "") {
    const n = Number(rawHierarchy);
    if (Number.isFinite(n)) roleHierarchy = n;
  }

  return {
    userId: typeof user.id === "number" ? user.id : null,
    tenantId:
      typeof user.tenantId === "number"
        ? user.tenantId
        : typeof role?.tenantId === "number"
          ? role.tenantId
          : null,
    roleId:
      typeof user.roleId === "number"
        ? user.roleId
        : typeof role?.id === "number"
          ? role.id
          : null,
    roleName: typeof role?.name === "string" ? role.name : null,
    roleSlug: typeof role?.slug === "string" ? role.slug : null,
    roleHierarchy,
    systemRole,
    permissionRecordId: typeof firstPerm?.id === "number" ? firstPerm.id : null,
    presetRole:
      (typeof ac?.presetRole === "string" ? ac.presetRole : null) ??
      (typeof firstPerm?.presetRole === "string" ? firstPerm.presetRole : null),
    isTenantAdmin: Boolean(user.isTenantAdmin) || systemRole === "TENANT_ADMIN",
    isSystemAdmin:
      Boolean(user.isSystemAdmin) ||
      systemRole === "SYSTEM_ADMIN" ||
      systemRole === "PLATFORM_STAFF",
  };
}

/** Hydrate all three RBAC state fields from a user object */
export function hydrateRbacFromUser(user: any) {
  if (!user) return { currentUser: null, accessConfig: null, context: null };
  return {
    currentUser: user,
    accessConfig: deriveAccessConfig(user),
    context: deriveUserContext(user),
  };
}
